import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import api from '../lib/api';
import { useI18n } from '../i18n/I18nContext';
import { useAuth } from '../context/AuthContext';
import { MaintenanceScreen } from '../components/MaintenanceScreen';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { SupportButton } from '../components/SupportButton';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Volleyball, ArrowLeft, ArrowRight, BookOpen, Heart, Target, Globe, Users, QrCode } from 'lucide-react';

const spring = { type: 'spring', stiffness: 300, damping: 20 };

const LEADS_OPEN = new Date() <= new Date('2026-11-10T23:59:59+01:00');

let settingsCache = null;
const loadSiteSettings = () => {
  if (!settingsCache) settingsCache = api.get('/settings').then((r) => r.data).catch(() => ({}));
  return settingsCache;
};

export const PublicLayout = ({ children, testid, pageKey }) => {
  const { t } = useI18n();
  const { user, loading: authLoading } = useAuth();
  const [blocked, setBlocked] = useState(false);
  const [site, setSite] = useState(null);
  useEffect(() => {
    loadSiteSettings().then((s) => {
      setSite(s);
      if (pageKey) setBlocked(s[pageKey] === '0');
    });
  }, [pageKey]);
  if (site?.maintenance_mode === '1' && !authLoading && user?.role !== 'admin') {
    return <MaintenanceScreen settings={site} />;
  }
  return (
    <div className="min-h-screen bg-nuit-900" data-testid={testid}>
      <header className="border-b border-nuit-700">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
          <Link to="/" data-testid="public-brand" className="flex items-center gap-2 min-w-0 font-fredoka font-semibold text-lg text-or-400">
            <span className="w-9 h-9 shrink-0 rounded-full bg-or-400 flex items-center justify-center">
              <Volleyball className="w-5 h-5 text-nuit-950" strokeWidth={2.5} />
            </span>
            <span className="truncate">On croit qu'on joue !</span>
          </Link>
          <div className="flex items-center gap-3 shrink-0">
            <LanguageSwitcher />
            <Link to="/" data-testid="public-back">
              <Button variant="ghost" className="rounded-full text-creme hover:bg-nuit-700 gap-2">
                <ArrowLeft className="w-4 h-4" strokeWidth={2.5} /> {t('common.home')}
              </Button>
            </Link>
          </div>
        </div>
      </header>
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        {blocked ? (
          <div className="text-center py-20 min-h-[50vh] flex flex-col items-center justify-center gap-6" data-testid="page-disabled">
            <p className="text-2xl font-bold text-creme font-fredoka">{t('page.disabled')}</p>
            <Link to="/" data-testid="page-disabled-home">
              <Button className="rounded-full bg-or-400 hover:bg-or-300 text-nuit-950 font-bold gap-2">
                <ArrowLeft className="w-4 h-4" strokeWidth={2.5} /> {t('common.home')}
              </Button>
            </Link>
          </div>
        ) : children}
      </main>
      <footer className="border-t border-nuit-700 py-8">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 flex flex-wrap gap-6 justify-center text-sm font-bold text-brume">
          {[
            { to: '/le-livre', key: 'page_livre', label: t('footer.book'), tid: 'footer-livre' },
            { to: '/medias', key: 'page_medias', label: t('footer.media'), tid: 'footer-medias' },
            { to: '/masterclass', key: 'page_masterclass', label: t('footer.masterclass'), tid: 'footer-masterclass' },
            { to: '/a-propos', key: 'page_apropos', label: t('footer.about'), tid: 'footer-apropos' },
            { to: '/faq', key: 'page_faq', label: t('footer.faq'), tid: 'footer-faq' },
            { to: '/confidentialite', key: null, label: t('footer.privacy'), tid: 'footer-privacy' },
            { to: '/mentions-legales', key: null, label: t('footer.legal'), tid: 'footer-legal' },
            { to: '/presse', key: 'page_presse', label: t('footer.press'), tid: 'footer-press' },
            { to: '/contact', key: 'page_contact', label: t('footer.contact'), tid: 'footer-contact' },
            { to: '/demande-acces', key: null, label: t('footer.access'), tid: 'footer-acces' },
          ].filter((l) => !l.key || !site || site[l.key] !== '0').map((l) => (
            <Link key={l.tid} to={l.to} className="hover:text-or-300" data-testid={l.tid}>{l.label}</Link>
          ))}
          <SupportButton variant="link" testid="support-project-link" />
        </div>
        <p className="text-center text-xs text-brume mt-4">{t('footer.rgpd')}</p>
      </footer>
    </div>
  );
};

function BookCodeForm() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setLoading(true);
    try {
      const { data } = await api.post('/auth/check-code', { code: code.trim() });
      navigate(data.role === 'jeune'
        ? `/inscription?code=${encodeURIComponent(code.trim())}`
        : `/inscription-adulte?code=${encodeURIComponent(code.trim())}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('landing.codeInvalid'));
    } finally {
      setLoading(false);
    }
  };
  return (
    <form onSubmit={submit} className="bg-nuit-800 border border-or-400/30 rounded-3xl p-6 sm:p-8 gold-glow" data-testid="book-code-form">
      <h2 className="font-fredoka font-semibold text-2xl text-creme mb-4">{t('book.codeTitle')}</h2>
      <label htmlFor="book-code" className="flex items-center gap-2 text-sm font-bold text-creme mb-3">
        <QrCode className="w-4 h-4 text-or-400" strokeWidth={2.5} />
        {t('landing.codeLabel')}
      </label>
      <div className="flex flex-col sm:flex-row gap-3">
        <Input id="book-code" data-testid="book-code-input" value={code}
          onChange={(e) => setCode(e.target.value)} placeholder={t('landing.codePlaceholder')}
          className="h-14 rounded-2xl text-base font-bold uppercase tracking-wider bg-nuit-900 border-nuit-600 text-creme placeholder:text-brume focus:ring-4 focus:ring-or-400/40" />
        <Button type="submit" data-testid="book-code-submit" disabled={loading}
          className="h-14 rounded-full px-8 bg-or-400 hover:bg-or-300 text-nuit-950 font-bold gap-2">
          {loading ? t('landing.checking') : t('landing.submit')}
          <ArrowRight className="w-5 h-5" strokeWidth={2.5} />
        </Button>
      </div>
      <p className="text-xs text-brume mt-4">{t('landing.qrHint')}</p>
      <p className="text-sm mt-3">
        <Link to="/demande-acces" data-testid="book-request-access"
          className="text-or-400 font-bold hover:text-or-300 hover:underline">
          {t('landing.noCode')}
        </Link>
      </p>
    </form>
  );
}

function GiveBackCounter() {
  const { t } = useI18n();
  const [community, setCommunity] = useState(null);
  useEffect(() => {
    api.get('/giveback/count').then((r) => setCommunity(r.data)).catch(() => {});
  }, []);
  return (
    <div className="bg-nuit-800 border border-nuit-700 rounded-3xl p-6 sm:p-8" data-testid="book-giveback-info">
      <h2 className="font-fredoka font-semibold text-2xl text-creme mb-4 flex items-center gap-2">
        <Heart className="w-5 h-5 text-or-400" strokeWidth={2.5} /> {t('book.givebackTitle')}
      </h2>
      <p className="text-base text-brume leading-relaxed">{t('landing.giveback.desc')}</p>
      {community && (community.givebacks > 0 || community.missions > 0) && (
        <p className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-or-300 bg-or-400/10 border border-or-400/30 rounded-full px-5 py-2"
          data-testid="book-community-counter">
          <Globe className="w-4 h-4" strokeWidth={2.5} />
          {t('landing.communityPrefix')} {[
            community.givebacks > 0 && `${community.givebacks} ${t(community.givebacks > 1 ? 'landing.givebackMany' : 'landing.givebackOne')}`,
            community.missions > 0 && `${community.missions} ${t(community.missions > 1 ? 'landing.missionMany' : 'landing.missionOne')}`,
          ].filter(Boolean).join(` ${t('landing.and')} `)} {t('landing.communitySuffix')}
        </p>
      )}
    </div>
  );
}

export default function BookPage() {
  const { t } = useI18n();
  return (
    <PublicLayout testid="book-page" pageKey="page_livre">
      <div className="grid lg:grid-cols-2 gap-12 items-center">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={spring}>
          <p className="text-or-400 font-bold tracking-widest text-sm uppercase">{t('book.eyebrow')}</p>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-creme mt-3">
            On croit qu'on joue !<br />
            <span className="text-or-400 text-3xl sm:text-4xl">{t('book.subtitle')}</span>
          </h1>
          <p className="text-base sm:text-lg text-brume leading-relaxed mt-6">
            {t('book.p1')}
          </p>
          <p className="text-base text-brume leading-relaxed mt-4">
            {t('book.p2')}
          </p>
          <div className="grid grid-cols-2 gap-4 mt-8">
            {[
              { icon: BookOpen, t: t('book.f1t'), d: t('book.f1d') },
              { icon: Heart, t: t('book.f2t'), d: t('book.f2d') },
              { icon: Users, t: t('book.f3t'), d: t('book.f3d') },
              { icon: Globe, t: t('book.f4t'), d: t('book.f4d') },
            ].map((f, i) => (
              <motion.div key={f.t} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
                transition={{ ...spring, delay: 0.15 + i * 0.08 }}
                className="bg-nuit-800 border border-nuit-700 rounded-2xl p-5">
                <f.icon className="w-5 h-5 text-or-400 mb-2" strokeWidth={2.5} />
                <p className="font-bold text-creme text-sm">{f.t}</p>
                <p className="text-xs text-brume mt-1">{f.d}</p>
              </motion.div>
            ))}
          </div>
          <Link to="/demande-acces" data-testid="book-cta">
            <Button className="mt-8 h-14 rounded-full bg-or-400 hover:bg-or-300 text-nuit-950 font-bold px-8 gap-2">
              {t('book.cta')} <Target className="w-5 h-5" strokeWidth={2.5} />
            </Button>
          </Link>
        </motion.div>
        <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ ...spring, delay: 0.15 }}>
          <img src="/images/couverture.jpg" alt="Couverture du roman On croit qu'on joue !"
            className="rounded-3xl ring-4 ring-or-400/30 shadow-[0_20px_50px_rgb(0,0,0,0.4)] w-full" />
        </motion.div>
      </div>

      {LEADS_OPEN && (
        <div className="grid md:grid-cols-2 gap-8 mt-16 items-stretch">
          <BookCodeForm />
          <GiveBackCounter />
        </div>
      )}
    </PublicLayout>
  );
}
